// Shared contract for the public-story scope of the global search dialog.
// Server and client both import from here so validation and shapes stay aligned,
// mirroring how people search shares src/lib/people-discovery.ts.

export const STORY_SEARCH_QUERY_MIN = 2;
export const STORY_SEARCH_QUERY_MAX = 80;
export const STORY_SEARCH_DEFAULT_LIMIT = 8;
export const STORY_SEARCH_MAX_LIMIT = 10;
const SNIPPET_MAX_LENGTH = 160;

export type StorySearchResult = {
  id: string;
  title: string;
  snippet: string;
  pillarTag: string | null;
  createdAt: string;
  author: {
    fullName: string;
    publicHandle: string | null;
  };
};

export type StorySearchValidation = { ok: true; query: string } | { ok: false };

export function normalizeStorySearchQuery(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim().replace(/\s+/g, ' ');
}

export function validateStorySearchQuery(value: unknown): StorySearchValidation {
  const query = normalizeStorySearchQuery(value);
  if (query.length < STORY_SEARCH_QUERY_MIN || query.length > STORY_SEARCH_QUERY_MAX) {
    return { ok: false };
  }
  return { ok: true, query };
}

/** Escapes ilike metacharacters so member input is always a literal match. */
export function escapeIlikePattern(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_');
}

function toSnippet(body: unknown): string {
  if (typeof body !== 'string') return '';
  const collapsed = body.replace(/\s+/g, ' ').trim();
  if (collapsed.length <= SNIPPET_MAX_LENGTH) return collapsed;
  return collapsed.slice(0, SNIPPET_MAX_LENGTH - 1).trimEnd() + '…';
}

type RawAuthor = { full_name?: unknown; public_handle?: unknown } | null | undefined;

export function mapStorySearchRows(value: unknown): StorySearchResult[] {
  if (!Array.isArray(value)) return [];
  const results: StorySearchResult[] = [];
  for (const row of value) {
    if (!row || typeof row !== 'object') continue;
    const record = row as {
      id?: unknown;
      title?: unknown;
      body?: unknown;
      pillar_tag?: unknown;
      created_at?: unknown;
      user_profiles?: RawAuthor | RawAuthor[];
    };
    if (typeof record.id !== 'string' || typeof record.title !== 'string') continue;
    const authorRaw = Array.isArray(record.user_profiles)
      ? record.user_profiles[0]
      : record.user_profiles;
    results.push({
      id: record.id,
      title: record.title,
      snippet: toSnippet(record.body),
      pillarTag: typeof record.pillar_tag === 'string' ? record.pillar_tag : null,
      createdAt: typeof record.created_at === 'string' ? record.created_at : '',
      author: {
        fullName: typeof authorRaw?.full_name === 'string' ? authorRaw.full_name : 'Ertzyx member',
        publicHandle: typeof authorRaw?.public_handle === 'string' ? authorRaw.public_handle : null,
      },
    });
  }
  return results;
}

export function readStorySearchResponse(value: unknown): StorySearchResult[] {
  if (!value || typeof value !== 'object') return [];
  return mapStorySearchRows((value as { results?: unknown }).results);
}
